import { ParsedQs, stringify } from 'qs';
import Config from '../Config';

import { ALLOWED_LANGUAGES, Language, languageSlug } from '../stores/LanguageStore';

export interface SearchParams {
  [param: string]: undefined | string | string[] | ParsedQs | ParsedQs[];
}

export const hasCurrentHost = (eventTarget: { hostname?: string; href?: string }): boolean => {
  if (eventTarget.hostname) {
    return isSameHost(eventTarget.hostname);
  }
  return isSameProtocolAndHost(eventTarget.href);
};

const isSameHost = (hostname: string) => {
  return hostname === window.location.hostname || (['local', 'ui-test'].includes(Config.env) && isDevHost(hostname));
};

const isDevHost = (hostname: string) => {
  return Config.devHostAliasNames && Config.devHostAliasNames.includes(hostname);
};

const isSameProtocolAndHost = (href: string) => {
  return (
    (href || '').startsWith(`${window.location.protocol}//${window.location.host}`) || (['local', 'ui-test'].includes(Config.env) && isDevProtocolAndHost(href))
  );
};

const isDevProtocolAndHost = (href: string) => {
  const link = extractHyperlink(href);
  return link.protocol === 'https:' && isDevHost(link.host);
};

const isExternalPath = (eventTarget: { href?: string }) => {
  const link = extractHyperlink(eventTarget.href);
  return Config.externalPaths && Config.externalPaths.some((ep) => pathnameWithoutLang(link.pathname).startsWith(`/${ep}`));
};

export const hasFilePath = (eventTarget: { pathname: string }): boolean => {
  return eventTarget.pathname !== null && /^.*\.[a-zA-Z0-9]*$/.test(eventTarget.pathname);
};

export const requiresLocalNavigation = (eventTarget: EventTarget) => {
  return hasCurrentHost(eventTarget) && !hasFilePath(eventTarget) && eventTarget.target !== '_blank' && !isExternalPath(eventTarget);
};

export const requiresSpinner = (eventTarget: { href?: string; target?: string }, isMyTeliaUserAgent?: boolean) => {
  if (isMyTeliaUserAgent) {
    return false;
  }
  return !!eventTarget && eventTarget.target !== '_blank' && !!eventTarget.href && !eventTarget.href.includes('mailto:');
};

export const hasModifierKey = (event: any) => {
  // justification for type any: there exist unrelated event classes defining these props
  return !!(event.ctrlKey || event.altKey || event.shiftKey || event.metaKey);
};

export const extractHyperlink = (url: string): HTMLHyperlinkElementUtils => {
  const link = document.createElement('a');
  link.href = url;
  return link;
};

export const putSearchIfMissing = (link: HTMLHyperlinkElementUtils, search: SearchParams) => {
  if (link.search && search) {
    console.warn(`Url ${link.href} already had search params, ignoring specified ${JSON.stringify(search)}`);
    return;
  }
  if (search) {
    link.search = stringify(search, {
      addQueryPrefix: true,
      arrayFormat: 'repeat'
    });
  }
};

export const putLangIfLocal = (link: HTMLHyperlinkElementUtils, lang: Language) => {
  if (!hasCurrentHost(link)) {
    console.warn(`Url ${link.href} is external, not adding language slug for lang ${lang}`);
    return;
  }

  const langSlug = languageSlug(lang);
  if (langSlug) {
    link.pathname = langSlug + toPathname(pathWithoutLang(toPath(link.pathname)));
  }
};

export const pathnameFromUrl = (url: string): string => {
  return extractHyperlink(url).pathname;
};

export const toPath = (pathname: string): string[] => {
  return pathname.split('/').filter((path) => path);
};

export const toPathname = (path: string[]): string => {
  return `/${path.join('/')}`;
};

export const pathStartsWithLang = (path: string[]): boolean => {
  return path && path.length > 0 && ALLOWED_LANGUAGES.includes(path[0]);
};

export const langFromPath = (path: string[]): Language => {
  return pathStartsWithLang(path) ? (path[0] as Language) : null;
};

export const langFromUrl = (url: string): Language => {
  return langFromPath(toPath(pathnameFromUrl(url)));
};

export const pathWithoutLang = (path: string[]): string[] => {
  return pathStartsWithLang(path) ? path.slice(1) : path;
};

export const pathnameWithoutLang = (pathname: string): string => {
  return toPathname(pathWithoutLang(toPath(pathname)));
};

export const urlWithLang = (url: string, lang: Language) => {
  const link = extractHyperlink(url || '');
  putLangIfLocal(link, lang);

  return url && url.startsWith(`${link.protocol}//${link.host}`) ? link.href : link.pathname + link.search + link.hash;
};

export const isRelativePath = (path: string): boolean => {
  return !path.startsWith('/');
};

export function parseParams(textValue: string, paramObject: any) {
  return Object.keys(paramObject).reduce((text, key) => text.replace(`$\{${key}}`, paramObject[key]), textValue);
}

export const prefixWithSlash = (pathName: string): string => {
  return pathName.length && !pathName.startsWith('/') ? `/${pathName}` : pathName;
};

export const removeParamsFromUrl = (params: string[]): string => {
  const replaceValue = window.location.search
    .split('&')
    .filter((search: string) => {
      return params.every((param) => !search.includes(param));
    })
    .join('&');
  return window.location.href.replace(window.location.search, replaceValue.startsWith('?') ? replaceValue : `?${replaceValue}`);
};

export const resolveNotFoundPagePath = (url: string): string => {
  const slicedPath = toPath(pathnameFromUrl(url)).slice(0, 2);
  const hasLang = pathStartsWithLang(slicedPath);
  const segment = hasLang ? slicedPath[1] : slicedPath[0];
  let notFoundPagePath = hasLang && slicedPath[0] !== 'et' ? `/${slicedPath[0]}` : '';
  if (['era', 'ari', 'partnerile'].includes(segment)) {
    notFoundPagePath = `${notFoundPagePath}/${segment}`;
  }
  return `${notFoundPagePath}_twebNotFound404`;
};
